import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { projectAPI, boardPreferencesAPI } from '../../services/api';
import {
  PROJECT_STATUSES as COLUMNS,
  TERMINAL_STATUSES as TERMINAL_COLUMNS,
  STATUS_KEY_BY_INDEX,
  STATUS_INDEX_BY_KEY,
} from '../../constants/projectStatus';
import { BOARD_CARD_FIELDS, DEFAULT_CARD_FIELDS, boardCardField } from '../../constants/boardCardFields';
import { PROJECT_TYPES } from '../../constants/projectTypes';
import BoardSettings from './BoardSettings';
import './projectBoard.css';

// Kanban job board: columns are ProjectStatus values, cards are jobs. Dragging a
// card to another column calls PATCH /projects/{id}/status, which fires that
// status's automations (same path as a manual status change). Native HTML5
// drag-and-drop — no extra dependency. Column config + numeric-index mapping live
// in the shared constants/projectStatus module (single source of truth).
//
// Layout — which stages show, their order, and what a card displays — is per user
// and saved server-side, so it follows someone between machines.

const daysInStatus = (statusChangedAt) => {
  if (!statusChangedAt) return null;
  const ms = Date.now() - new Date(statusChangedAt).getTime();
  return Math.max(0, Math.floor(ms / (1000 * 60 * 60 * 24)));
};

const move = (list, idx, delta) => {
  const target = idx + delta;
  if (target < 0 || target >= list.length) return list;
  const next = [...list];
  [next[idx], next[target]] = [next[target], next[idx]];
  return next;
};

const ProjectBoard = ({ onViewProject }) => {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showTerminal, setShowTerminal] = useState(false);
  const [draggingId, setDraggingId] = useState(null);
  const [dragOverKey, setDragOverKey] = useState(null);

  // Layout preferences.
  const [hiddenStatuses, setHiddenStatuses] = useState([]);
  const [statusOrder, setStatusOrder] = useState([]);
  const [cardFields, setCardFields] = useState(DEFAULT_CARD_FIELDS);
  // ProjectType keys to show. Empty = all types, which is the default and matches
  // how the board behaved before this filter existed.
  const [jobTypes, setJobTypes] = useState([]);
  const [showSettings, setShowSettings] = useState(false);
  const [savingPrefs, setSavingPrefs] = useState(false);
  // Nothing is saved until the user actually changes something — otherwise simply
  // opening the board would write a preferences row for everyone.
  const prefsLoaded = useRef(false);

  const loadProjects = useCallback(async () => {
    try {
      setLoading(true);
      const res = await projectAPI.getAll();
      setProjects(res.data || []);
    } catch (err) {
      console.error('Failed to load board:', err);
      setError('Could not load the job board.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadProjects(); }, [loadProjects]);

  useEffect(() => {
    let cancelled = false;
    boardPreferencesAPI.get()
      .then(res => {
        if (cancelled) return;
        const { hiddenStatuses: hidden, statusOrder: order, cardFields: fields, jobTypes: types } = res.data || {};
        setHiddenStatuses(hidden || []);
        setStatusOrder(order || []);
        setJobTypes(types || []);
        setCardFields(fields?.length ? fields : DEFAULT_CARD_FIELDS);
      })
      .catch(err => console.error('Failed to load board preferences:', err))
      .finally(() => { prefsLoaded.current = true; });
    return () => { cancelled = true; };
  }, []);

  const persist = useCallback(async (next) => {
    if (!prefsLoaded.current) return;
    try {
      setSavingPrefs(true);
      await boardPreferencesAPI.save({
        hiddenStatuses: next.hiddenStatuses ?? hiddenStatuses,
        statusOrder: next.statusOrder ?? statusOrder,
        cardFields: next.cardFields ?? cardFields,
        jobTypes: next.jobTypes ?? jobTypes,
      });
    } catch (err) {
      console.error('Failed to save board preferences:', err);
      setError('Could not save your board layout.');
      setTimeout(() => setError(''), 4000);
    } finally {
      setSavingPrefs(false);
    }
  }, [hiddenStatuses, statusOrder, cardFields, jobTypes]);

  const statusKey = (p) => STATUS_KEY_BY_INDEX[p.status] ?? 'Lead';

  // Base set, then the user's order, then their hidden list. A status missing from
  // statusOrder keeps its default position rather than jumping to one end, so a new
  // pipeline stage doesn't land somewhere surprising for people who've customised.
  const orderedColumns = useMemo(() => {
    const base = showTerminal ? [...COLUMNS, ...TERMINAL_COLUMNS] : COLUMNS;
    if (!statusOrder.length) return base;

    const rank = new Map(statusOrder.map((key, i) => [key, i]));
    return [...base].sort((a, b) => {
      const ra = rank.has(a.key) ? rank.get(a.key) : Infinity;
      const rb = rank.has(b.key) ? rank.get(b.key) : Infinity;
      if (ra !== rb) return ra - rb;
      return base.indexOf(a) - base.indexOf(b);
    });
  }, [showTerminal, statusOrder]);

  const typeKeyOf = (p) => PROJECT_TYPES.find(t => t.index === p.type)?.key ?? null;

  // Empty jobTypes = every type. Filtering to nothing would show an empty board
  // with no explanation, so "none selected" means "no filter".
  const typeFilteredProjects = useMemo(() => {
    if (!jobTypes.length) return projects;
    const wanted = new Set(jobTypes);
    return projects.filter(p => wanted.has(typeKeyOf(p)));
  }, [projects, jobTypes]);

  const hiddenSet = useMemo(() => new Set(hiddenStatuses), [hiddenStatuses]);
  const visibleColumns = useMemo(
    () => orderedColumns.filter(c => !hiddenSet.has(c.key)),
    [orderedColumns, hiddenSet]
  );

  // Jobs sitting in a hidden stage. Surfaced as a count rather than silently
  // dropped — "my job vanished off the board" is a bad afternoon.
  const hiddenJobCount = useMemo(
    () => typeFilteredProjects.filter(p => hiddenSet.has(statusKey(p))).length,
    [projects, hiddenSet]
  );

  const toggleHidden = (key) => {
    const next = hiddenSet.has(key) ? hiddenStatuses.filter(k => k !== key) : [...hiddenStatuses, key];
    setHiddenStatuses(next);
    persist({ hiddenStatuses: next });
  };

  const moveColumn = (idx, delta) => {
    const next = move(orderedColumns, idx, delta).map(c => c.key);
    setStatusOrder(next);
    persist({ statusOrder: next });
  };

  const toggleField = (key) => {
    const next = cardFields.includes(key) ? cardFields.filter(k => k !== key) : [...cardFields, key];
    setCardFields(next);
    persist({ cardFields: next });
  };

  const toggleJobType = (key) => {
    const next = jobTypes.includes(key) ? jobTypes.filter(k => k !== key) : [...jobTypes, key];
    setJobTypes(next);
    persist({ jobTypes: next });
  };

  const moveField = (idx, delta) => {
    const next = move(cardFields, idx, delta);
    setCardFields(next);
    persist({ cardFields: next });
  };

  const resetPrefs = async () => {
    try {
      setSavingPrefs(true);
      await boardPreferencesAPI.reset();
      setHiddenStatuses([]);
      setStatusOrder([]);
      setCardFields(DEFAULT_CARD_FIELDS);
    } catch (err) {
      console.error('Failed to reset board preferences:', err);
    } finally {
      setSavingPrefs(false);
    }
  };

  const onDragStart = (e, id) => {
    setDraggingId(id);
    e.dataTransfer.effectAllowed = 'move';
  };

  const onDrop = async (e, colKey) => {
    e.preventDefault();
    setDragOverKey(null);
    const id = draggingId;
    setDraggingId(null);
    if (!id) return;

    const project = projects.find((p) => p.id === id);
    if (!project || statusKey(project) === colKey) return;

    // Optimistic move; the API returns the new status string. Revert on failure.
    const prev = projects;
    const targetIndex = STATUS_INDEX_BY_KEY[colKey];
    setProjects((list) =>
      list.map((p) =>
        p.id === id ? { ...p, status: Number(targetIndex), statusChangedAt: new Date().toISOString() } : p
      )
    );
    try {
      await projectAPI.updateStatus(id, colKey);
    } catch (err) {
      setProjects(prev); // revert
      setError(err.response?.data?.message || 'Could not move that job.');
      setTimeout(() => setError(''), 4000);
    }
  };

  // Card body from the user's chosen fields. Fields with no value are skipped so a
  // card never carries an empty row.
  const renderCardFields = (p, days, stale) => {
    const metas = [];
    const texts = [];

    for (const key of cardFields) {
      const field = boardCardField(key);
      if (!field) continue;

      if (field.special === 'days') {
        if (days !== null) {
          metas.push(
            <span key={key} className={`board-card-days${stale ? ' stale' : ''}`} title="Days in this stage">
              {days}d
            </span>
          );
        }
        continue;
      }

      const value = field.render(p);
      if (!value) continue;

      if (field.kind === 'meta') {
        metas.push(<span key={key} className="board-card-meta-item">{value}</span>);
      } else {
        texts.push(<div key={key} className="board-card-text" title={value}>{value}</div>);
      }
    }

    return (
      <>
        {texts}
        {metas.length > 0 && <div className="board-card-meta">{metas}</div>}
      </>
    );
  };

  if (loading) return <div className="board-loading">Loading board…</div>;

  return (
    <div className="project-board-wrap">
      <div className="board-toolbar">
        <label className="board-terminal-toggle">
          <input type="checkbox" checked={showTerminal} onChange={(e) => setShowTerminal(e.target.checked)} />
          Show closed / cancelled
        </label>

        {/* Job-type filter. JobNimbus used a separate board per job type; this is
            the same thing as a filter on the shared pipeline, so a repair tech can
            narrow to repairs without a second board to keep in sync. Persisted with
            the rest of the layout, so it follows the user between machines. */}
        <div className="board-type-filter">
          {PROJECT_TYPES.map(t => (
            <button
              key={t.key}
              type="button"
              className={`board-type-chip${jobTypes.includes(t.key) ? ' active' : ''}`}
              onClick={() => toggleJobType(t.key)}
              title={jobTypes.includes(t.key) ? `Stop showing only ${t.label}` : `Show only ${t.label}`}
            >
              {t.label}
            </button>
          ))}
          {jobTypes.length > 0 && (
            <button
              type="button"
              className="board-type-chip board-type-clear"
              onClick={() => { setJobTypes([]); persist({ jobTypes: [] }); }}
              title="Show all job types"
            >
              Clear
            </button>
          )}
        </div>

        <button className="board-settings-trigger" onClick={() => setShowSettings(true)}>
          ⚙ Board Layout
        </button>

        {hiddenJobCount > 0 && (
          <span className="board-hidden-note">
            {hiddenJobCount} job{hiddenJobCount === 1 ? '' : 's'} in hidden stages
          </span>
        )}

        {error && <span className="board-error">{error}</span>}
      </div>

      <div className="project-board">
        {visibleColumns.map((col) => {
          const cards = typeFilteredProjects.filter((p) => statusKey(p) === col.key);
          const total = cards.reduce((sum, p) => sum + (p.estimatedCost || 0), 0);
          return (
            <div
              key={col.key}
              className={`board-column${dragOverKey === col.key ? ' drag-over' : ''}`}
              onDragOver={(e) => { e.preventDefault(); setDragOverKey(col.key); }}
              onDragLeave={() => setDragOverKey((k) => (k === col.key ? null : k))}
              onDrop={(e) => onDrop(e, col.key)}
            >
              <div className="board-col-header" style={{ borderTopColor: col.accent }}>
                <span className="board-col-title">{col.label}</span>
                <span className="board-col-count">{cards.length}</span>
              </div>
              {total > 0 && (
                <div className="board-col-total">${total.toLocaleString()}</div>
              )}
              <div className="board-col-cards">
                {cards.map((p) => {
                  const days = daysInStatus(p.statusChangedAt);
                  const stale = days !== null && days >= 14;
                  return (
                    <div
                      key={p.id}
                      className={`board-card${draggingId === p.id ? ' dragging' : ''}`}
                      draggable
                      onDragStart={(e) => onDragStart(e, p.id)}
                      onDragEnd={() => setDraggingId(null)}
                      onClick={() => onViewProject && onViewProject(p.id)}
                    >
                      <div className="board-card-title">{p.projectName}</div>
                      {renderCardFields(p, days, stale)}
                    </div>
                  );
                })}
                {cards.length === 0 && <div className="board-col-empty">Drop here</div>}
              </div>
            </div>
          );
        })}
      </div>

      {showSettings && (
        <BoardSettings
          columns={orderedColumns}
          hidden={hiddenSet}
          cardFields={cardFields.filter(k => BOARD_CARD_FIELDS.some(f => f.key === k))}
          onToggleHidden={toggleHidden}
          onMoveColumn={moveColumn}
          onToggleField={toggleField}
          onMoveField={moveField}
          onReset={resetPrefs}
          onClose={() => setShowSettings(false)}
          saving={savingPrefs}
        />
      )}
    </div>
  );
};

export default ProjectBoard;
